import { motion } from "framer-motion";
import { Check, ArrowUpRight, Workflow, Users } from "lucide-react";
import { IMAGES } from "../../data/content";

const products = [
  {
    id: "formflow",
    icon: Workflow,
    tagline: "Internal Tools, Reimagined.",
    name: "FormFlo",
    image: IMAGES.formflow,
    description:
      "The low-code/no-code orchestration platform that lets you build powerful internal applications without writing a single line of software.",
    tags: ["Low-Code", "Orchestration", "RBAC"],
    cta: { label: "Request a Demo" },
    features: [
      {
        title: "Dynamic Workflows & Approvals",
        desc: "Automate complex business logic instantly with visual rule builders.",
      },
      {
        title: "Metadata-Driven Architecture",
        desc: "Scalable, flexible, and ultra-fast deployment with zero rebuilds.",
      },
      {
        title: "Enterprise Security (RBAC)",
        desc: "Secure by design with precise Role-Based Access Control.",
      },
      {
        title: "Predictable Pricing",
        desc: "Simple, transparent annual billing to keep software costs stable.",
      },
    ],
  },
  {
    id: "memberspace",
    icon: Users,
    tagline: "India's First AI-Enabled Governance Platform.",
    name: "MemberSpace",
    image: IMAGES.memberspace,
    description:
      "One platform for every member group — residential communities, temple trusts, alumni networks, sports clubs and cooperative societies. Run governance, finances, elections and communication professionally, all in one place.",
    tags: ["Governance", "AI-Enabled", "Communities"],
    cta: { label: "Explore MemberSpace", href: "https://memberspace.app" },
    features: [
      {
        title: "Member Management",
        desc: "Complete directory with photos, documents, member types, roles & status.",
      },
      {
        title: "Digital Resolutions & Elections",
        desc: "OTP-verified approvals, transparent elections, GBM & remote voting with full audit trails.",
      },
      {
        title: "Finance, Dues & Donations",
        desc: "Multi-fund ledger, maintenance dues, receipts, donations and expense approval workflows.",
      },
      {
        title: "AI Ask Documents",
        desc: "Ask plain-English questions across your entire community document archive.",
      },
    ],
  },
];

export const Products = () => {
  return (
    <section
      id="products"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#0F172A]/30"
      data-testid="products-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4]">
            What We Build
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            A Product Portfolio Built on Intelligence
          </h2>
          <p className="mt-4 text-base text-[#94A3B8] leading-relaxed">
            Two flagship platforms engineered to replace operational complexity
            with elegant, automated systems.
          </p>
        </motion.div>

        <div className="mt-16 space-y-24">
          {products.map((p, idx) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
              data-testid={`product-${p.id}`}
            >
              {/* Image */}
              <div className={`relative ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative rounded-3xl overflow-hidden border border-white/10 glow-indigo">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-[340px] sm:h-[420px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/70 to-transparent" />
                  <div className="absolute top-5 left-5 flex gap-2 flex-wrap">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="text-xs font-medium px-3 py-1 rounded-full bg-[#020617]/70 border border-white/10 text-[#06B6D4] backdrop-blur-sm"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className={idx % 2 === 1 ? "lg:order-1" : ""}>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#06B6D4] flex items-center justify-center">
                    <p.icon className="text-white" size={22} />
                  </div>
                  <span className="text-sm font-medium text-[#06B6D4]">
                    {p.tagline}
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-3xl font-semibold text-white">
                  {p.name}
                </h3>
                <p className="mt-3 text-[#94A3B8] leading-relaxed">
                  {p.description}
                </p>

                <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-5">
                  {p.features.map((f) => (
                    <div key={f.title} className="flex gap-3">
                      <div className="mt-0.5 h-5 w-5 rounded-full bg-[#6366F1]/20 flex items-center justify-center shrink-0">
                        <Check size={12} className="text-[#06B6D4]" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white">
                          {f.title}
                        </div>
                        <div className="text-sm text-[#64748B] leading-snug">
                          {f.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {p.cta?.href ? (
                  <a
                    href={p.cta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 group inline-flex items-center gap-2 text-white font-medium hover:text-[#06B6D4] transition-colors"
                    data-testid={`product-cta-${p.id}`}
                  >
                    {p.cta.label}
                    <ArrowUpRight
                      size={18}
                      className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                  </a>
                ) : (
                  <button
                    onClick={() =>
                      document
                        .querySelector("#contact")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="mt-8 group inline-flex items-center gap-2 text-white font-medium hover:text-[#06B6D4] transition-colors"
                    data-testid={`product-cta-${p.id}`}
                  >
                    {p.cta?.label || "Request a Demo"}
                    <ArrowUpRight
                      size={18}
                      className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
